import { useState } from "react";
import API from "../../lib/api";
import { Plus, Trash, PencilSimple, Tag } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../../components/ui/dialog";

export default function CategoriesTab({ categories, onReload }) {
  const [categoryModal, setCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", slug: "", description: "", display_order: "0", visible: true });
  const [savingCategory, setSavingCategory] = useState(false);

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const openCreate = () => {
    setEditingCategory(null);
    setCategoryForm({ name: "", slug: "", description: "", display_order: String(categories.length + 1), visible: true });
    setCategoryModal(true);
  };

  const openEdit = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name, slug: cat.slug, description: cat.description || "",
      display_order: String(cat.display_order || 0), visible: cat.visible !== false
    });
    setCategoryModal(true);
  };

  const handleSave = async () => {
    setSavingCategory(true);
    try {
      const payload = {
        ...categoryForm,
        display_order: parseInt(categoryForm.display_order) || 0,
        slug: categoryForm.slug || autoSlug(categoryForm.name)
      };
      if (editingCategory) {
        await API.put(`/admin/categories/${editingCategory.id}`, payload);
      } else {
        await API.post("/admin/categories", payload);
      }
      setCategoryModal(false);
      onReload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      console.error("Failed to save category:", err);
      alert(typeof detail === "string" ? detail : "Failed to save category");
    }
    setSavingCategory(false);
  };

  const handleDelete = async (catId) => {
    if (!window.confirm("Delete this category? Products using it will keep their current category slug.")) return;
    try {
      await API.delete(`/admin/categories/${catId}`);
      onReload();
    } catch (err) {
      console.error("Failed to delete category:", err);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <div>
          <p className="text-sm text-[#A1A1AA] font-light">{categories.length} categories</p>
          <p className="text-xs text-zinc-600 font-light mt-1">Manage your store categories. Changes are reflected across the site instantly.</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-2.5 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300" data-testid="add-category-btn">
          <Plus size={14} /> Add Category
        </button>
      </div>
      <div className="space-y-3" data-testid="admin-categories-list">
        {categories.map(cat => (
          <div key={cat.id} className="flex items-center gap-4 bg-[#121212] border border-[rgba(212,175,55,0.1)] p-5" data-testid={`admin-category-${cat.id}`}>
            <Tag size={18} weight="thin" className="text-[#D4AF37] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <p className="text-sm text-[#FDFBF7] font-light">{cat.name}</p>
                <span className="text-[10px] text-[#A1A1AA] bg-[#0a0a0a] px-2 py-0.5 font-mono">{cat.slug}</span>
                {!cat.visible && <span className="text-[10px] text-red-400 border border-red-400/30 px-2 py-0.5 uppercase tracking-wider">Hidden</span>}
              </div>
              {cat.description && <p className="text-xs text-[#A1A1AA] font-light mt-1">{cat.description}</p>}
            </div>
            <span className="text-xs text-[#A1A1AA] font-light">Order: {cat.display_order}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => openEdit(cat)} className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors p-2" data-testid={`edit-category-${cat.id}`}><PencilSimple size={16} weight="thin" /></button>
              <button onClick={() => handleDelete(cat.id)} className="text-[#A1A1AA] hover:text-red-400 transition-colors p-2" data-testid={`delete-category-${cat.id}`}><Trash size={16} weight="thin" /></button>
            </div>
          </div>
        ))}
        {categories.length === 0 && (
          <p className="text-[#A1A1AA] text-sm font-light py-8 text-center">No categories yet. Add your first category to get started.</p>
        )}
      </div>

      {/* Category Modal */}
      <Dialog open={categoryModal} onOpenChange={setCategoryModal}>
        <DialogContent className="bg-[#121212] border-[rgba(212,175,55,0.2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7]">
              {editingCategory ? "Edit Category" : "Add Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4" data-testid="category-form">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Name</label>
              <input value={categoryForm.name} onChange={e => { const name = e.target.value; setCategoryForm(p => ({ ...p, name, slug: editingCategory ? p.slug : autoSlug(name) })); }} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" placeholder="e.g. Candles" data-testid="category-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Slug (URL-friendly)</label>
              <input value={categoryForm.slug} onChange={e => setCategoryForm(p => ({ ...p, slug: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light font-mono" placeholder="e.g. candles" data-testid="category-slug-input" />
              <p className="text-[10px] text-zinc-600 mt-1">Used in URLs: /shop?category={categoryForm.slug || "..."}</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Description</label>
              <input value={categoryForm.description} onChange={e => setCategoryForm(p => ({ ...p, description: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" placeholder="Optional description" data-testid="category-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Display Order</label>
                <input type="number" value={categoryForm.display_order} onChange={e => setCategoryForm(p => ({ ...p, display_order: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="category-order-input" />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] font-light cursor-pointer">
                  <input type="checkbox" checked={categoryForm.visible} onChange={e => setCategoryForm(p => ({ ...p, visible: e.target.checked }))} className="accent-[#D4AF37]" data-testid="category-visible-checkbox" /> Visible on site
                </label>
              </div>
            </div>
            <button onClick={handleSave} disabled={savingCategory || !categoryForm.name || !categoryForm.slug} className="w-full bg-[#D4AF37] text-black py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300 disabled:opacity-50 mt-4" data-testid="save-category-btn">
              {savingCategory ? "Saving..." : editingCategory ? "Update Category" : "Create Category"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
